import type { InputBaseProps, SxProps, Theme } from "@mui/material"
import { Box } from "@mui/material"
import { MuiTelInput } from "mui-tel-input"
import React, { forwardRef } from "react"
import type { ComponentProps, LabelHTMLAttributes, ReactNode } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { Controller } from "react-hook-form"

import { FIELD_LABEL_CLASSES } from "@/constants/forms"

interface PhoneNumberInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label: string
  required?: boolean
  containerProps?: ComponentProps<typeof Box>
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>
  inputProps?: InputBaseProps["inputProps"]
  sx?: SxProps<Theme>
  endAdornment?: ReactNode
  placeholder?: string
}

const PhoneNumberInputComponent = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (
    {
      control,
      name,
      label,
      required = false,
      containerProps = {},
      labelProps = {},
      inputProps = {},
      sx,
      endAdornment,
      placeholder,
    },
    ref
  ) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? "Phone number is required" : false,
          validate: (value) => {
            if (!value) return true
            return value.trim() !== "" || "Phone number is invalid"
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Box className='relative w-full' {...containerProps}>
            {label && (
              <label className={FIELD_LABEL_CLASSES} htmlFor={field.name} {...labelProps}>
                {label} {required && <span className='text-red-600'>*</span>}
              </label>
            )}
            <MuiTelInput
              sx={sx}
              id={field.name}
              {...field}
              preferredCountries={["US", "CA", "IN", "CN", "GB", "IT", "MY", "AX", "KR", "MX", "AL"]}
              forceCallingCode
              defaultCountry='US'
              error={!!error}
              helperText={error?.message}
              fullWidth
              inputRef={ref}
              placeholder={placeholder}
              InputProps={{
                endAdornment: endAdornment,
              }}
              inputProps={inputProps}
            />
          </Box>
        )}
      />
    )
  }
)

PhoneNumberInputComponent.displayName = "PhoneNumberInput"

// Export as a properly generic component
export const PhoneNumberInput = PhoneNumberInputComponent as <TFieldValues extends FieldValues = FieldValues>(
  props: PhoneNumberInputProps<TFieldValues> & { ref?: React.Ref<HTMLInputElement> }
) => React.ReactElement
